"use strict";

exports.__esModule = true;
exports.CLASSIFICATION_BATCH_QUIET_MS = exports.CLASSIFICATION_BATCH_MAX_WAIT_MS = void 0;
exports.CurrentClassificationProvider = CurrentClassificationProvider;
var _react = _interopRequireWildcard(require("react"));
var _CurrentClassificationContext = require("./CurrentClassificationContext");
var _CurrentClassificationSubscriptions = require("./CurrentClassificationSubscriptions");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function _interopRequireWildcard(e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, "default": e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (var _t in e) "default" !== _t && {}.hasOwnProperty.call(e, _t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, _t)) && (i.get || i.set) ? o(f, _t, i) : f[_t] = e[_t]); return f; })(e, t); }
function _createForOfIteratorHelperLoose(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (t) return (t = t.call(r)).next.bind(t); if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var o = 0; return function () { return o >= r.length ? { done: !0 } : { done: !1, value: r[o++] }; }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
/** Wait for a quiet gap before publishing batched state updates. */
var CLASSIFICATION_BATCH_QUIET_MS = exports.CLASSIFICATION_BATCH_QUIET_MS = 250;
/** Flush pending updates even if packets keep arriving. */
var CLASSIFICATION_BATCH_MAX_WAIT_MS = exports.CLASSIFICATION_BATCH_MAX_WAIT_MS = 1000;
function normalizeDeviceUuids(deviceUuids) {
  if (deviceUuids === void 0) {
    deviceUuids = [];
  }
  var seen = new Set();
  var unique = [];
  for (var _iterator = _createForOfIteratorHelperLoose(deviceUuids), _step; !(_step = _iterator()).done;) {
    var uuid = _step.value;
    if (!uuid || seen.has(uuid)) {
      continue;
    }
    seen.add(uuid);
    unique.push(uuid);
  }
  return unique;
}
function computeUnion(registrations) {
  var seen = new Set();
  var union = [];
  for (var _iterator2 = _createForOfIteratorHelperLoose(registrations.values()), _step2; !(_step2 = _iterator2()).done;) {
    var deviceUuids = _step2.value;
    for (var _iterator3 = _createForOfIteratorHelperLoose(deviceUuids), _step3; !(_step3 = _iterator3()).done;) {
      var uuid = _step3.value;
      if (seen.has(uuid)) {
        continue;
      }
      seen.add(uuid);
      union.push(uuid);
    }
  }
  return union.sort();
}

/**
 * Owns one current-classification TimelineSubscription per registered device
 * for all widgets under a layout.
 *
 * Live packets are coalesced: publish after a quiet gap, or at maxWait if the
 * stream stays busy, so a burst of device changes causes one React update.
 */
function CurrentClassificationProvider(_ref) {
  var children = _ref.children;
  var registrationsRef = (0, _react.useRef)(new Map());
  var nextRegistrationIdRef = (0, _react.useRef)(0);
  var pendingStatesRef = (0, _react.useRef)({});
  var quietTimerRef = (0, _react.useRef)(undefined);
  var maxWaitTimerRef = (0, _react.useRef)(undefined);
  var _useState = (0, _react.useState)([]),
    subscribedDeviceUuids = _useState[0],
    setSubscribedDeviceUuids = _useState[1];
  var _useState2 = (0, _react.useState)({}),
    deviceStates = _useState2[0],
    setDeviceStates = _useState2[1];
  var flushPendingStates = (0, _react.useCallback)(function () {
    if (quietTimerRef.current) {
      window.clearTimeout(quietTimerRef.current);
      quietTimerRef.current = undefined;
    }
    if (maxWaitTimerRef.current) {
      window.clearTimeout(maxWaitTimerRef.current);
      maxWaitTimerRef.current = undefined;
    }
    var pending = pendingStatesRef.current;
    if (Object.keys(pending).length === 0) {
      return;
    }
    pendingStatesRef.current = {};
    setDeviceStates(function (previous) {
      var changed = false;
      var next = Object.assign({}, previous);
      for (var _i = 0, _Object$entries = Object.entries(pending); _i < _Object$entries.length; _i++) {
        var _Object$entries$_i = _Object$entries[_i],
          deviceUuid = _Object$entries$_i[0],
          state = _Object$entries$_i[1];
        if (next[deviceUuid] !== state) {
          next[deviceUuid] = state;
          changed = true;
        }
      }
      return changed ? next : previous;
    });
  }, []);
  (0, _react.useEffect)(function () {
    return function () {
      if (quietTimerRef.current) {
        window.clearTimeout(quietTimerRef.current);
      }
      if (maxWaitTimerRef.current) {
        window.clearTimeout(maxWaitTimerRef.current);
      }
    };
  }, []);
  var syncSubscribedDeviceUuids = (0, _react.useCallback)(function () {
    var next = computeUnion(registrationsRef.current);
    setSubscribedDeviceUuids(function (previous) {
      return previous.length === next.length && previous.every(function (uuid, index) {
        return uuid === next[index];
      }) ? previous : next;
    });
  }, []);
  var registerDevices = (0, _react.useCallback)(function (deviceUuids) {
    if (deviceUuids === void 0) {
      deviceUuids = [];
    }
    var registrationId = ++nextRegistrationIdRef.current;
    registrationsRef.current.set(registrationId, normalizeDeviceUuids(deviceUuids));
    syncSubscribedDeviceUuids();
    return function () {
      registrationsRef.current["delete"](registrationId);
      syncSubscribedDeviceUuids();
    };
  }, [syncSubscribedDeviceUuids]);
  var handleDeviceState = (0, _react.useCallback)(function (deviceUuid, packet) {
    var _packet$value;
    var state = packet == null || (_packet$value = packet.value) == null ? void 0 : _packet$value.state;
    if (!state) {
      return;
    }
    pendingStatesRef.current[deviceUuid] = state;
    if (quietTimerRef.current) {
      window.clearTimeout(quietTimerRef.current);
    }
    quietTimerRef.current = window.setTimeout(flushPendingStates, CLASSIFICATION_BATCH_QUIET_MS);
    if (!maxWaitTimerRef.current) {
      maxWaitTimerRef.current = window.setTimeout(flushPendingStates, CLASSIFICATION_BATCH_MAX_WAIT_MS);
    }
  }, [flushPendingStates]);
  var value = (0, _react.useMemo)(function () {
    return {
      deviceStates: deviceStates,
      registerDevices: registerDevices
    };
  }, [deviceStates, registerDevices]);
  return /*#__PURE__*/_react["default"].createElement(_CurrentClassificationContext.CurrentClassificationContext.Provider, {
    value: value
  }, /*#__PURE__*/_react["default"].createElement(_CurrentClassificationSubscriptions.CurrentClassificationSubscriptions, {
    deviceUuids: subscribedDeviceUuids,
    onDeviceState: handleDeviceState
  }), children);
}